import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useCraqueBagre } from '../hooks/useCraqueBagre';
import type { FutebolEventoPartida, DadosJogadorHT } from '@/shared/types';
import { soundEngine } from '@/shared/lib/soundEngine';

// ── Foto do jogador ───────────────────────────────────────────────────────────

function FotoJogador({ src, nome, size }: { src: string | null; nome: string; size: string }) {
  const [erro, setErro] = useState(false);
  const st = { width: size, height: size };

  if (!src || erro) {
    return (
      <div
        className="rounded-full bg-white/10 flex items-center justify-center font-bold text-white/40 uppercase"
        style={{ ...st, fontSize: 'clamp(25px, 2.5vw, 40px)' }}
      >
        {nome.slice(0, 2)}
      </div>
    );
  }
  return (
    <img
      src={src} alt={nome} style={st}
      className="rounded-full object-cover"
      onError={() => setErro(true)}
    />
  );
}

// ── Nota numérica com cor ─────────────────────────────────────────────────────

function NotaBadge({ nota, tipo }: { nota: number; tipo: 'craque' | 'bagre' }) {
  const cor = tipo === 'craque' ? '#fbbf24' : '#f87171';
  return (
    <div
      className="flex items-center justify-center rounded-full font-black"
      style={{
        width: 'clamp(48px, 4.5vw, 80px)', height: 'clamp(48px, 4.5vw, 80px)',
        fontSize: 'clamp(16px, 1.5vw, 24px)', color: cor,
        background: tipo === 'craque' ? 'rgba(251,191,36,0.12)' : 'rgba(239,68,68,0.12)',
        border: `2px solid ${tipo === 'craque' ? 'rgba(251,191,36,0.4)' : 'rgba(239,68,68,0.4)'}`,
      }}
    >
      {nota.toFixed(1)}
    </div>
  );
}

// ── Card lateral do jogador ───────────────────────────────────────────────────

function CardJogador({
  jogador, tipo, label, emoji,
}: { jogador: DadosJogadorHT; tipo: 'craque' | 'bagre'; label: string; emoji: string }) {
  const corTitulo = tipo === 'craque' ? '#fbbf24' : '#f87171';
  return (
    <motion.div
      className="flex flex-col items-center gap-2"
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: tipo === 'bagre' ? 1.5 : 0 }}
    >
      <p style={{ fontSize: 'clamp(9px, 0.8vw, 14px)', fontWeight: 900, color: corTitulo, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
        {emoji} {label}
      </p>
      <div className="relative">
        <FotoJogador src={jogador.fotoUrl} nome={jogador.nome} size="clamp(100px, 10vw, 160px)" />
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
          <NotaBadge nota={jogador.nota} tipo={tipo} />
        </div>
      </div>
      <p style={{ fontSize: 'clamp(12px, 1.1vw, 20px)', fontWeight: 700, color: 'white', marginTop: 'clamp(8px, 0.8vw, 16px)', textAlign: 'center', maxWidth: '14vw', lineHeight: 1.3 }}>
        {jogador.nome}
      </p>
    </motion.div>
  );
}

// ── Barra de votação animada ──────────────────────────────────────────────────

function BarraVotacao({ pctConcorda, totalVotos, tempoRestanteSeg, duracaoSeg }: {
  pctConcorda: number; totalVotos: number; tempoRestanteSeg: number; duracaoSeg: number;
}) {
  const pctDiscorda = 100 - pctConcorda;
  const pctTempo    = duracaoSeg > 0 ? (tempoRestanteSeg / duracaoSeg) * 100 : 0;
  const votacaoEncerrada = tempoRestanteSeg <= 0;

  return (
    <div className="flex flex-col gap-2" style={{ minWidth: 'clamp(280px, 28vw, 400px)' }}>
      {/* Resultado */}
      <div className="text-center" style={{ marginBottom: 'clamp(6px, 0.6vw, 12px)' }}>
        <p style={{ fontSize: 'clamp(8px, 0.75vw, 14px)', fontWeight: 900, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          O salão vota
        </p>
        {votacaoEncerrada ? (
          <p className="animate-pulse" style={{ fontSize: 'clamp(22px, 2.2vw, 36px)', fontWeight: 900, color: pctConcorda > 50 ? '#fbbf24' : '#f87171', lineHeight: 1.1, marginTop: '4px' }}>
            {pctConcorda > 50 ? '👑 CRAQUE' : pctConcorda < 50 ? '🐟 BAGRE' : '⚖️ EMPATE'}
          </p>
        ) : (
          <p style={{ fontSize: 'clamp(24px, 2.4vw, 42px)', fontWeight: 900, color: 'white', lineHeight: 1.1 }}>
            {pctConcorda}% <span style={{ color: '#fbbf24' }}>Craque</span>
          </p>
        )}
        <p style={{ fontSize: 'clamp(9px, 0.8vw, 15px)', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>
          {totalVotos} {totalVotos === 1 ? 'voto' : 'votos'} {votacaoEncerrada ? 'registrados' : 'até agora'}
        </p>
      </div>

      {/* Barra concorda vs discorda */}
      <div style={{ height: 'clamp(12px, 1.2vw, 20px)', borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pctConcorda}%`,
            background: 'linear-gradient(90deg, #10b981, #fbbf24)',
          }}
        />
      </div>
      <div className="flex justify-between" style={{ fontSize: 'clamp(8px, 0.75vw, 14px)', fontWeight: 700 }}>
        <span style={{ color: '#fbbf24' }}>👑 {pctConcorda}% Craque</span>
        <span style={{ color: '#f87171' }}>🐟 {pctDiscorda}% Bagre</span>
      </div>

      {/* Barra de tempo restante */}
      <div style={{ height: 'clamp(4px, 0.4vw, 8px)', borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.06)', marginTop: 'clamp(6px, 0.6vw, 12px)' }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${pctTempo}%`,
            background: 'rgba(251,191,36,0.5)',
            transition: 'width 1s linear',
          }}
        />
      </div>
      <p style={{ fontSize: 'clamp(7px, 0.65vw, 12px)', color: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>
        {votacaoEncerrada ? 'Votação encerrada' : `${Math.floor(tempoRestanteSeg / 60)}:${String(tempoRestanteSeg % 60).padStart(2, '0')} restantes`}
      </p>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

const DURACAO_HT_SEG = 15 * 60;

interface Props {
  telaId:              string;
  fixtureId:           number | null;
  ultimoEvento:        FutebolEventoPartida;
  initialIntervalKey?: string | null;
  onIntervalProcessado?: (key: string) => void;
}

export default function TvIntervaloOverlay({ telaId, fixtureId, ultimoEvento, initialIntervalKey, onIntervalProcessado }: Props) {
  const { dados, pctConcorda, totalVotos } = useCraqueBagre(telaId, fixtureId, ultimoEvento);
  const [saindo,          setSaindo]          = useState(false);
  const [tempoRestante,   setTempoRestante]   = useState(DURACAO_HT_SEG);
  const [duracaoTotal,    setDuracaoTotal]    = useState(DURACAO_HT_SEG);

  // Toca a fanfarra uma única vez por intervalo (identificado pela `expiracao`).
  // lastPlayedKeyRef persiste a chave do intervalo para o qual o som já tocou,
  // inicializado com o valor vindo de Player.tsx para sobreviver a remounts.
  const lastPlayedKeyRef = useRef<string | null>(initialIntervalKey ?? null);
  const onIntervalProcessadoRef = useRef(onIntervalProcessado);
  useEffect(() => { onIntervalProcessadoRef.current = onIntervalProcessado; });

  useEffect(() => {
    if (!dados) return;
    if (lastPlayedKeyRef.current === dados.expiracao) return;
    lastPlayedKeyRef.current = dados.expiracao;
    onIntervalProcessadoRef.current?.(dados.expiracao);
    soundEngine.playSound('craque_bagre_intervalo');
  }, [dados]);

  // Countdown baseado na expiração real gravada pelo backend
  useEffect(() => {
    if (!dados) { setSaindo(false); return; }

    const expMs = new Date(dados.expiracao).getTime();
    // duracaoTotal é sempre a duração fixa do intervalo (15 min = DURACAO_HT_SEG).
    // tempoRestante/duracaoTotal fornece a proporção correta para a barra de progresso
    // mesmo quando o TV reinicia no meio do intervalo.
    setDuracaoTotal(DURACAO_HT_SEG);

    const tick = () => {
      const restante = Math.max(0, Math.round((expMs - Date.now()) / 1000));
      setTempoRestante(restante);
      // Mantém a tela por mais 4 segundos após zerar para verem o resultado
      if (restante <= -4) setSaindo(true);
    };
    tick();
    const t = setInterval(tick, 1_000);
    return () => clearInterval(t);
  }, [dados]);

  if (!dados) return null;

  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 flex flex-col items-center justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: saindo ? 0 : 1 }}
        transition={{ duration: saindo ? 0.5 : 0.4 }}
        style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(12px)' }}
      >
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', damping: 20, stiffness: 200, delay: 0.1 }}
          style={{
            marginBottom: 'clamp(20px, 3vw, 40px)',
            fontSize: 'clamp(24px, 3vw, 48px)',
            fontWeight: 900,
            color: 'rgba(255,255,255,0.9)',
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            textShadow: '0 4px 20px rgba(0,0,0,0.5)',
          }}
        >
          Intervalo
        </motion.div>

        <div
          className="flex items-center gap-8 px-10 py-8 rounded-3xl"
          style={{
            background: 'linear-gradient(160deg, rgba(14,12,4,0.97) 0%, rgba(18,14,4,0.95) 100%)',
            border:     '1.5px solid rgba(251,191,36,0.3)',
            maxWidth:   '80vw',
          }}
        >
          {/* Craque */}
          <CardJogador jogador={dados.craque} tipo="craque" label="Craque do 1º tempo" emoji="👑" />

          {/* Centro: barra de votação */}
          <BarraVotacao
            pctConcorda={pctConcorda}
            totalVotos={totalVotos}
            tempoRestanteSeg={Math.max(0, tempoRestante)}
            duracaoSeg={duracaoTotal}
          />

          {/* Bagre */}
          <CardJogador jogador={dados.bagre} tipo="bagre" label="Bagre do 1º tempo" emoji="🐟" />
        </div>

        {/* Instrução para celular */}
        <p
          className="absolute"
          style={{
            bottom: '4vw', left: '50%', transform: 'translateX(-50%)',
            fontSize: 'clamp(14px, 1.2vw, 24px)', color: 'rgba(255,255,255,0.6)', fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          📱 Vote pelo celular apontando para o QR Code
        </p>
      </motion.div>
    </>
  );
}
